import { useState } from 'react';
import { useTheme } from '../../styles/ThemeContext';
import { Field } from '../../components/forms/Field';

const emptyCustom = { category: 'Custom', product: '', coo: '', hs_code: '', value: '', dg: 'No', export_type: 'Temporary' };

/**
 * Multi-select product modal for adding products to a shipping unit
 * Side-by-side layout: product catalog (left) + selected products (right)
 */
export function ProductSelectionModal({ isOpen, onClose, products = [], onConfirm, onSaveCustomProduct, onEditCustomProduct, onDeleteCustomProduct, unitLabel }) {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [selectedProds, setSelectedProds] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [custom, setCustom] = useState({ ...emptyCustom });
  const [customError, setCustomError] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const { theme } = useTheme();
  const c = theme.colors;

  if (!isOpen) return null;

  const updateCustom = (field, value) => setCustom(prev => ({ ...prev, [field]: value }));

  const addCustomProduct = () => {
    if (!custom.product.trim()) { setCustomError('Product name is required'); return; }
    if (editingProduct) {
      const updated = { ...editingProduct, category: custom.category.trim() || 'Custom', product: custom.product.trim(), coo: custom.coo.trim().toUpperCase(), hs_code: custom.hs_code.trim(), value: Number(custom.value) || 0, dg: custom.dg, export_type: custom.export_type };
      if (onEditCustomProduct) onEditCustomProduct(updated);
      setEditingProduct(null);
    } else {
      const customProd = { id: -Date.now(), category: custom.category.trim() || 'Custom', group: '', product: custom.product.trim(), coo: custom.coo.trim().toUpperCase(), hs_code: custom.hs_code.trim(), value: Number(custom.value) || 0, dg: custom.dg, export_type: custom.export_type, _isCustom: true };
      setSelectedProds(prev => [...prev, { product: customProd, qty: 1, eq: '', wt: '' }]);
      if (onSaveCustomProduct) onSaveCustomProduct(customProd);
    }
    setCustom({ ...emptyCustom }); setCustomError(''); setShowCustomForm(false);
  };

  const startEditCustom = (p, e) => {
    e.stopPropagation();
    setEditingProduct(p);
    setCustom({ category: p.category || 'Custom', product: p.product || '', coo: p.coo || '', hs_code: p.hs_code || '', value: p.value || '', dg: p.dg || 'No', export_type: p.export_type || 'Temporary' });
    setShowCustomForm(true); setCustomError('');
  };

  const deleteCustom = (p, e) => {
    e.stopPropagation();
    if (!confirm('Delete custom product "' + p.product + '"?')) return;
    if (onDeleteCustomProduct) onDeleteCustomProduct(p);
    setSelectedProds(prev => prev.filter(s => s.product.id !== p.id));
  };

  const safeProds = Array.isArray(products) ? products : [];
  const categories = [];
  const catSet = new Set();
  safeProds.forEach((p) => { const cat = p?.category || 'Uncategorized'; if (!catSet.has(cat)) { catSet.add(cat); categories.push(cat); } });
  categories.sort();

  let filteredList = safeProds;
  if (filterCat) filteredList = filteredList.filter((p) => (p?.category || 'Uncategorized') === filterCat);
  if (search) { const sl = search.toLowerCase(); filteredList = filteredList.filter((p) => (p?.product || '').toLowerCase().includes(sl) || (p?.category || '').toLowerCase().includes(sl)); }

  const grouped = {};
  filteredList.forEach((p) => { const cat = p?.category || 'Uncategorized'; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(p); });

  const toggleProduct = (p) => { setSelectedProds((prev) => { const idx = prev.findIndex((s) => s.product.id === p.id); if (idx >= 0) return prev.filter((_, i) => i !== idx); return [...prev, { product: p, qty: 1, eq: '', wt: '' }]; }); };
  const updateSelProd = (index, field, value) => { setSelectedProds((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))); };
  const removeSelProd = (index) => { setSelectedProds((prev) => prev.filter((_, i) => i !== index)); };

  const handleConfirm = () => { if (selectedProds.length === 0) return; onConfirm(selectedProds); setSelectedProds([]); setSearch(''); setFilterCat(''); setShowCustomForm(false); setCustom({ ...emptyCustom }); setCustomError(''); setEditingProduct(null); };
  const handleClose = () => { setSelectedProds([]); setSearch(''); setFilterCat(''); setShowCustomForm(false); setCustom({ ...emptyCustom }); setCustomError(''); setEditingProduct(null); onClose(); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: c.overlay, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: c.bgCard, border: '1px solid ' + c.border, width: '100%', maxWidth: 1100, maxHeight: '92vh', display: 'flex', flexDirection: 'column', borderRadius: 4 }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + c.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>🔍 Select Product {unitLabel && `for ${unitLabel}`}</div>
          <button onClick={handleClose} style={{ ...theme.btnG, padding: '4px 10px', fontSize: 12 }}>✕</button>
        </div>

        {/* Search + Custom form */}
        <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search product..." autoFocus style={{ ...theme.inp, flex: 1, minWidth: 150 }} />
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ ...theme.sel, width: 180 }}>
              <option value="">All categories</option>
              {categories.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
            </select>
            <button onClick={() => { setShowCustomForm(v => !v); setCustomError(''); setEditingProduct(null); setCustom({ ...emptyCustom }); }} style={{ ...theme.btnP, fontSize: 11, padding: '6px 12px', whiteSpace: 'nowrap' }}>{showCustomForm ? '✕ Close Form' : '+ Custom Product'}</button>
          </div>

          {showCustomForm && (
            <div style={{ border: '1px solid ' + c.accent, borderRadius: 4, padding: 14, marginBottom: 12, background: c.accentBg }}>
              <div style={{ fontSize: 10, fontFamily: 'monospace', color: c.accent, letterSpacing: 1, marginBottom: 10 }}>{editingProduct ? 'EDIT CUSTOM PRODUCT' : 'CUSTOM PRODUCT'}</div>
              {customError && <div style={{ fontSize: 11, color: c.error, marginBottom: 8, fontFamily: 'monospace' }}>⚠ {customError}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <Field l="Category"><input value={custom.category} onChange={(e) => updateCustom('category', e.target.value)} placeholder="e.g. Electronics" style={theme.inp} /></Field>
                <Field l="Product *"><input value={custom.product} onChange={(e) => { updateCustom('product', e.target.value); setCustomError(''); }} placeholder="Product name" style={{ ...theme.inp, borderColor: customError ? c.error : undefined }} /></Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 8 }}>
                <Field l="Country of Origin"><input value={custom.coo} onChange={(e) => updateCustom('coo', e.target.value.slice(0, 2))} placeholder="NL" maxLength={2} style={{ ...theme.inp, textTransform: 'uppercase' }} /></Field>
                <Field l="HS Code"><input value={custom.hs_code} onChange={(e) => updateCustom('hs_code', e.target.value)} placeholder="e.g. 8471.30" style={theme.inp} /></Field>
                <Field l="Value (EUR)"><input type="number" min="0" step="0.01" value={custom.value} onChange={(e) => updateCustom('value', e.target.value)} placeholder="0.00" style={theme.inp} /></Field>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, alignItems: 'end' }}>
                <Field l="DG (Dangerous Goods)"><select value={custom.dg} onChange={(e) => updateCustom('dg', e.target.value)} style={theme.sel}><option value="No">No</option><option value="Yes">Yes</option></select></Field>
                <Field l="Export Type"><select value={custom.export_type} onChange={(e) => updateCustom('export_type', e.target.value)} style={theme.sel}><option value="Temporary">Temporary</option><option value="Permanent">Permanent</option></select></Field>
                <button onClick={addCustomProduct} style={{ ...theme.btnP, padding: '8px 16px', fontSize: 12, height: 34 }}>{editingProduct ? '✓ Save Changes' : '✓ Add Custom Product'}</button>
              </div>
            </div>
          )}
        </div>

        {/* Main content: two columns */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* LEFT: Product catalog */}
          <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 14px', borderRight: '1px solid ' + c.border }}>
            <div style={{ border: '1px solid ' + c.border, borderRadius: 3 }}>
              {Object.keys(grouped).length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: c.textSecondary, fontSize: 12 }}>No products found</div>
              ) : (
                Object.entries(grouped).map(([cat, ps]) => (
                  <div key={cat}>
                    <div style={{ fontSize: 9, fontFamily: 'monospace', color: c.accent, letterSpacing: 1, padding: '5px 10px', background: c.bgInput, borderBottom: '1px solid ' + c.border, borderTop: '1px solid ' + c.border, position: 'sticky', top: 0, zIndex: 1 }}>{cat.toUpperCase()}</div>
                    {ps.map((p, i) => {
                      const isSel = selectedProds.some((s) => s.product.id === p.id);
                      return (
                        <div key={p.id || i} onClick={() => toggleProduct(p)} style={{ padding: '7px 10px', cursor: 'pointer', background: isSel ? c.accentBg : 'transparent', borderBottom: '1px solid ' + c.border, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 16, height: 16, borderRadius: 3, border: isSel ? '2px solid ' + c.accent : '2px solid ' + c.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: c.accent, flexShrink: 0 }}>{isSel ? '✓' : ''}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                              {p.product || 'Unknown Product'}
                              {p._isCustom && <span style={{ fontSize: 8, background: c.infoBg, color: c.info, border: '1px solid ' + c.infoBorder, padding: '1px 5px', borderRadius: 2, fontFamily: 'monospace', fontWeight: 500 }}>Custom</span>}
                            </div>
                            <div style={{ fontSize: 9, color: c.textSecondary, fontFamily: 'monospace', marginTop: 1 }}>{p.group || ''} · CoO: {p.coo || 'N/A'} · HS: {p.hs_code || 'N/A'} · €{p.value || 0}</div>
                          </div>
                          {p.dg === 'Yes' && <span style={{ fontSize: 8, background: c.errorBg, color: c.error, border: '1px solid ' + c.errorBorder, padding: '1px 4px', borderRadius: 2, fontFamily: 'monospace', flexShrink: 0 }}>⚠ DG</span>}
                          {p._isCustom && (
                            <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                              <button onClick={(e) => startEditCustom(p, e)} title="Edit" style={{ background: 'transparent', border: '1px solid ' + c.infoBorder, color: c.info, padding: '1px 5px', borderRadius: 2, cursor: 'pointer', fontSize: 9, fontFamily: 'monospace' }}>✎</button>
                              <button onClick={(e) => deleteCustom(p, e)} title="Delete" style={{ background: 'transparent', border: '1px solid ' + c.errorBorder, color: c.error, padding: '1px 5px', borderRadius: 2, cursor: 'pointer', fontSize: 9, fontFamily: 'monospace' }}>✕</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RIGHT: Selected products (always visible) */}
          <div style={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid ' + c.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 10, fontFamily: 'monospace', color: c.accent, letterSpacing: 1, fontWeight: 600 }}>
                SELECTED ({selectedProds.length})
              </div>
              {selectedProds.length > 0 && (
                <button onClick={() => setSelectedProds([])} style={{ background: 'transparent', border: 'none', color: c.textSecondary, cursor: 'pointer', fontSize: 10, fontFamily: 'monospace' }}>Clear all</button>
              )}
            </div>

            <div style={{ flex: 1, overflow: 'auto', padding: '0 0 8px' }}>
              {selectedProds.length === 0 ? (
                <div style={{ padding: '40px 16px', textAlign: 'center', color: c.textSecondary }}>
                  <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>📋</div>
                  <div style={{ fontSize: 11, lineHeight: 1.5 }}>Click products on the left to select them</div>
                </div>
              ) : (
                selectedProds.map((sp, idx) => (
                  <div key={sp.product.id || idx} style={{ padding: '10px 14px', borderBottom: '1px solid ' + c.border }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {sp.product.product || 'Unknown'}
                        </div>
                        <div style={{ fontSize: 8, color: c.textSecondary, fontFamily: 'monospace' }}>
                          {sp.product.category || 'N/A'} · €{sp.product.value || 0}{sp.product.dg === 'Yes' ? ' · ⚠ DG' : ''}
                        </div>
                      </div>
                      <button onClick={() => removeSelProd(idx)} style={{ background: 'transparent', border: '1px solid ' + c.errorBorder, color: c.error, padding: '1px 5px', borderRadius: 2, cursor: 'pointer', fontSize: 9, fontFamily: 'monospace', flexShrink: 0 }} title="Remove">✕</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                      <Field l="Qty *"><input type="number" min="1" value={sp.qty} onChange={(e) => updateSelProd(idx, 'qty', Math.max(1, Number(e.target.value)))} style={{ ...theme.inp, padding: '4px 6px', fontSize: 11 }} /></Field>
                      <Field l="EQ #"><input value={sp.eq} onChange={(e) => updateSelProd(idx, 'eq', e.target.value)} placeholder="—" style={{ ...theme.inp, padding: '4px 6px', fontSize: 11 }} /></Field>
                      <Field l="Weight"><input type="number" value={sp.wt} onChange={(e) => updateSelProd(idx, 'wt', e.target.value)} placeholder="—" style={{ ...theme.inp, padding: '4px 6px', fontSize: 11 }} /></Field>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid ' + c.border, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
          <button onClick={handleClose} style={theme.btnG}>Cancel</button>
          <button onClick={handleConfirm} disabled={selectedProds.length === 0} style={{ ...theme.btnP, opacity: selectedProds.length > 0 ? 1 : 0.4, cursor: selectedProds.length > 0 ? 'pointer' : 'not-allowed' }}>✓ Add {selectedProds.length > 0 ? selectedProds.length + ' ' : ''}{selectedProds.length === 1 ? 'Product' : 'Products'} to Unit</button>
        </div>
      </div>
    </div>
  );
}
